import PropTypes from 'prop-types';
import Entity from '@/common-utils/components/renders/entity/Entity';
import TooltipHelper from '@/common-utils/components/renders/tooltip/TooltipHelper';
import getTranslatedText from '@/common-utils/functions/getTranslatedText';
import { nsCustomization } from '@/app/i18n/namespaces';
import customizationFields from '@/app/modules/payludd/customization/fields/customizationFields';

const PAYMENT_TRIGGERS_PREFIX = 'details.functionnalities.payment_triggers';
const EXTERNAL_TRIGGER_PREFIX = `${PAYMENT_TRIGGERS_PREFIX}.options.externalTrigger`;

const MODE_ENTITY_BY_SUFFIX = {
    CASHIER: `${EXTERNAL_TRIGGER_PREFIX}.title`,
    MANUEL: `${PAYMENT_TRIGGERS_PREFIX}.options.amountScreenDisplay.title`,
    DONS: `${PAYMENT_TRIGGERS_PREFIX}.options.donationMode.title`,
    AUTOMATE: `${PAYMENT_TRIGGERS_PREFIX}.options.automateMode.title`
};

const EXTERNAL_TRIGGER_OPTIONS_WITH_HOVER = new Set([
    customizationFields.MPF_OPTIONS.CONCERT_PROTOCOL_V3_2.key,
    customizationFields.MPF_OPTIONS.CONCERT_PROTOCOL_V2_USB.key,
    customizationFields.MPF_OPTIONS.CONCERT_PROTOCOL_V2.key
]);

/**
 * @param {string} modeSuffix
 * @returns {string|undefined}
 */
function getModeEntity(modeSuffix) {
    return MODE_ENTITY_BY_SUFFIX[modeSuffix];
}

/**
 * @param {string} optionKey
 * @returns {JSX}
 */
function renderExternalTriggerOption(optionKey) {
    const label = getTranslatedText(`${EXTERNAL_TRIGGER_PREFIX}.${optionKey}`, nsCustomization);

    if (!EXTERNAL_TRIGGER_OPTIONS_WITH_HOVER.has(optionKey)) {
        return <span>{label}</span>;
    }

    return (
        <TooltipHelper
            trigger={<span className='cursor-default'>{label}</span>}
            content={
                <Entity
                    entity={`${EXTERNAL_TRIGGER_PREFIX}.hover.${optionKey}`}
                    namespace={nsCustomization}
                    className='text-sm'
                />
            }
        />
    );
}

/**
 * @param root0
 * @param root0.value
 * @returns {JSX|null}
 */
function RenderPayluddMpfMode({ value }) {
    if (!value) {
        return null;
    }

    if (typeof value === 'string') {
        const modeEntity = getModeEntity(value);
        if (!modeEntity) {
            return null;
        }
        return (
            <Entity
                className='font-bold'
                entity={modeEntity}
                namespace={nsCustomization}
            />
        );
    }

    const { mode, enabledOptions = [], allConfigurationsEnabled, port, speedRate } = value;
    const modeEntity = getModeEntity(mode);
    const detailsLabel = allConfigurationsEnabled
        ? getTranslatedText(`${EXTERNAL_TRIGGER_PREFIX}.all_cashier_options`, nsCustomization)
        : null;

    const configParts = [];
    if (port !== undefined) {
        configParts.push(`${getTranslatedText(`${PAYMENT_TRIGGERS_PREFIX}.keys.port`, nsCustomization)} ${port}`);
    }

    const speedRateLabel = speedRate !== undefined
        ? (
            <span>
                <TooltipHelper
                    trigger={
                        <span className='cursor-default'>
                            {getTranslatedText(`${PAYMENT_TRIGGERS_PREFIX}.keys.exchange_speed`, nsCustomization)}
                        </span>
                    }
                    content={
                        <Entity
                            entity={`${PAYMENT_TRIGGERS_PREFIX}.keys.exchange_speed_hover`}
                            namespace={nsCustomization}
                            className='text-sm'
                        />
                    }
                />
                {`: ${speedRate} bps`}
            </span>
        )
        : null;

    return (
        <div className='flex flex-col gap-1 font-bold'>
            <div className='flex flex-wrap items-baseline gap-x-1.5'>
                {modeEntity && (
                    <Entity
                        entity={modeEntity}
                        namespace={nsCustomization}
                    />
                )}
                {detailsLabel && (
                    <>
                        <span>-</span>
                        <span>{detailsLabel}</span>
                    </>
                )}
                {!detailsLabel && enabledOptions.length > 0 && (
                    <>
                        <span>-</span>
                        <span className='inline-flex flex-wrap items-baseline gap-x-1'>
                            {enabledOptions.map((optionKey, index) => (
                                <span key={optionKey} className='inline-flex items-baseline'>
                                    {index > 0 && <span className='mr-1'>/</span>}
                                    {renderExternalTriggerOption(optionKey)}
                                </span>
                            ))}
                        </span>
                    </>
                )}
            </div>
            {(configParts.length > 0 || speedRateLabel) && (
                <div className='flex flex-col'>
                    {configParts.map((configPart) => (
                        <span key={configPart}>{configPart}</span>
                    ))}
                    {speedRateLabel}
                </div>
            )}
        </div>
    );
}

RenderPayluddMpfMode.propTypes = {
    value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            mode: PropTypes.string,
            enabledOptions: PropTypes.arrayOf(PropTypes.string),
            allConfigurationsEnabled: PropTypes.bool,
            port: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
            speedRate: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
        })
    ])
};

export default RenderPayluddMpfMode;
